import type { ProxyWidgetData } from '@trinity/management-app-client/app-v1/pages';
import type { ConnectionRequest, WidgetDataSource } from './dashboard-config.js';
import type { CanonicalWidgetData } from './dashboard-data.js';

/**
 * What a client asks the widget data proxy for: one variant per remote source type. Static sources have no
 * request; their data is already in the page config.
 */
export type AgentWidgetDataRequest = { type: 'agent'; agentId: number; extensionPath: string };
export type ConnectionWidgetDataRequest = {
  type: 'connection';
  connectionIdentifier: string;
  request: ConnectionRequest;
};
export type WidgetDataRequest = AgentWidgetDataRequest | ConnectionWidgetDataRequest;

/** A proxy call: the request plus the context the proxy resolves server-side. */
export type WidgetDataProxyCall = WidgetDataRequest & {
  /** When set, the proxy resolves the brand and sends it with the request. */
  brandIdentifier?: string;
  /**
   * The application `brandIdentifier` is used in. Only a caller that can't send the scope headers itself needs to
   * set it (the management builder, through management-api); the chat app's headers carry the scope.
   */
  applicationIdentifier?: string;
  subject?: { type: string; id: number };
};

/** Fetches remote widget data through the host app's backend proxy. */
export type WidgetDataProxy = (call: WidgetDataProxyCall) => Promise<CanonicalWidgetData | null>;

/** The proxy request a widget's data source makes, or null for a static source (nothing to fetch). */
export function widgetDataRequestFromSource(source: WidgetDataSource): WidgetDataRequest | null {
  switch (source.type) {
    case 'static':
      return null;
    case 'agent':
      return { type: 'agent', agentId: source.agentId, extensionPath: source.extensionPath };
    case 'connection':
      return { type: 'connection', connectionIdentifier: source.connectionIdentifier, request: source.request };
    default: {
      const unsupported: never = source;
      throw new Error(`Unsupported widget data source: ${JSON.stringify(unsupported)}`);
    }
  }
}

/**
 * The HTTP body for `POST /pages/widget-data-proxy`. An agent call keeps the original body, with no `type`,
 * so it stays valid for servers that predate `connection` and the server rejects a `type` on that shape.
 */
export function toProxyWidgetDataBody(call: WidgetDataProxyCall): ProxyWidgetData.Request {
  const { brandIdentifier, subject } = call;
  switch (call.type) {
    case 'agent':
      return { agentId: call.agentId, extensionPath: call.extensionPath, brandIdentifier, subject };
    case 'connection':
      return {
        type: 'connection',
        connectionIdentifier: call.connectionIdentifier,
        request: call.request,
        brandIdentifier,
        applicationIdentifier: call.applicationIdentifier,
        subject,
      };
    default: {
      const unsupported: never = call;
      throw new Error(`Unsupported widget data request: ${JSON.stringify(unsupported)}`);
    }
  }
}
